/* eslint-disable @typescript-eslint/no-explicit-any */

import { isUserSponsoredContentUser, getRolesInPartnerAccount } from '@services/excalibur-service';
import { lookupUser } from '@services/identity-service';
import {
  type PrincipalUserData,
  getJwtFromToken,
  getPrincipalToken,
  getApiClientAccessToken,
} from '@services/identity-token-service';
import { type IPartnerDetails, getPartnerAccount } from '@services/partner-service';
import { getConfigData, type IEnabledAdProducts } from '@services/platform-configuration-sync';
import { getUserProfile } from '@services/user-profile-service';
import { PARTNER_TYPE } from '@stores/user-store';
import { getTraceId } from '@utils/trace-utils';
import { getConfig } from 'bernie-config';
import { SystemEventLevel } from 'bernie-logger';
import { serializeError } from 'serialize-error';
import { ERROR } from 'src/constants';

export interface IPartnerSelect {
  id: string;
  name: string;
}

export interface IPartnerConfig {
  isOnboarded: boolean;
  partnerType: PARTNER_TYPE;
  enabledAdProducts: IEnabledAdProducts;
  isMigrated: boolean;
}

export interface IUser {
  userJwt: PrincipalUserData;
  profile: IUserProfile;
  partner: IPartnerDetails;
  roles: string[];
  sponsoredContentUser: boolean;
  adminContext: boolean;
  partnerConfig: IPartnerConfig;
}

interface IUserProfile {
  firstName: string;
  lastName: string;
  primaryEmail: string;
  initials: string;
}

export interface IdentityUtilsRequest {
  headers?: any;
  log?: (...args: any[]) => void;
  [key: string]: any;
}

const safeGetTraceId = (request: IdentityUtilsRequest): string => {
  try {
    return getTraceId(request as any) || 'unknown-trace-id';
  } catch {
    return 'unknown-trace-id';
  }
};

const resolveProfileData = async (request: IdentityUtilsRequest, encodedJwt: string, userId: string): Promise<any> => {
  let profileData = await getUserProfile(request as any, encodedJwt, userId);
  // It's possible users wont show up if they haven't been migrated from
  // legacy FERRIS pool to newer BEX pool
  // Not sure when this will change

  /**
   * Handling a few scenarios here:
   *  1 - Possible user won't show up if they are in the legacy FERRIS pool, so doing a user lookup
   *  2 - Users created via UPL dont seem to have an email, so doing a user lookup and using that email
   */

  const email = profileData?.data?.contact_info?.primary_email?.email;

  if (!profileData || !profileData.data || !email) {
    const user = await lookupUser(request as any, encodedJwt, userId);

    // Profile exists but no email
    if (profileData?.data && !email && user['identifier']['email']) {
      profileData.data.contact_info['primary_email'] = {
        email: user['identifier']['email'],
      };
    } else {
      // No profile returned
      const [ferrisProfile, expediaProfile] = await Promise.all([
        user?.legacy_user_ids?.FERRIS?.egaid
          ? getUserProfile(request as any, encodedJwt, user.legacy_user_ids.FERRIS.egaid)
          : Promise.resolve(null),
        user?.legacy_user_ids?.EXPEDIA?.egaid
          ? getUserProfile(request as any, encodedJwt, user.legacy_user_ids.EXPEDIA.egaid)
          : Promise.resolve(null),
      ]);

      profileData = ferrisProfile || expediaProfile;
    }

    // if still no profile data, return empty to not break the app
    if (!profileData) {
      profileData = {
        data: {
          basic_info: {
            first_name: '',
            last_name: '',
          },
          contact_info: {
            primary_email: {
              email: '',
            },
          },
        },
      };
    }
  }

  return profileData;
};

export const getUserInfo = async (request: IdentityUtilsRequest): Promise<IUser> => {
  try {
    const {
      app: { sponsoredContentPartnerAccountId },
    } = getConfig();
    const principalToken = await getPrincipalToken(request as any);
    const principalJwt = getJwtFromToken(principalToken.encodedJwt);

    const [profileData, partnerData, userRolesInPartnerAccount, isSponsoredContentUser, partnerConfigData] =
      await Promise.all([
        resolveProfileData(request, principalToken.encodedJwt, principalJwt.sub),
        getPartnerAccount(request as any, principalToken.encodedJwt, principalJwt.partner_account_id),
        getRolesInPartnerAccount(request as any, principalToken, principalJwt.partner_account_id, true),
        isUserSponsoredContentUser(request as any, principalToken),
        getConfigData(request as any, principalToken.encodedJwt, principalJwt.partner_account_id),
      ]);

    const profile: IUserProfile = {
      firstName: profileData.data.basic_info.first_name,
      lastName: profileData.data.basic_info.last_name,
      primaryEmail: profileData.data.contact_info.primary_email?.email || '',
      initials: profileData.data.basic_info.first_name[0] + profileData.data.basic_info.last_name[0],
    };

    const enabledAdProducts = partnerConfigData?.partner_account_configuration?.enabled_ad_products;

    const lslEnabled =
      !!enabledAdProducts?.TRAVEL_ADS?.ad_product_preferences?.enabled_ad_types?.LODGING_SPONSORED_LISTING ||
      !!enabledAdProducts?.VRBO_ADS?.ad_product_preferences?.enabled_ad_types?.LODGING_SPONSORED_LISTING;

    // default to FLIGHT_SPONSORED_LISTING if no partner type is found
    const partnerConfig: IPartnerConfig = {
      isOnboarded: partnerConfigData?.partner_account_configuration?.is_onboarded || false,
      partnerType: lslEnabled ? PARTNER_TYPE.LODGING_SPONSORED_LISTING : PARTNER_TYPE.FLIGHT_SPONSORED_LISTING,
      enabledAdProducts: enabledAdProducts || {},
      isMigrated: partnerConfigData?.lodging_sponsored_listing?.is_migrated || false,
    };

    return {
      userJwt: principalJwt,
      profile,
      partner: partnerData,
      roles: userRolesInPartnerAccount.map((role) => role.id),
      sponsoredContentUser: isSponsoredContentUser,
      adminContext: partnerData.account_id === sponsoredContentPartnerAccountId,
      partnerConfig,
    };
  } catch (error) {
    const traceId = safeGetTraceId(request);
    request?.log?.([ERROR, 'IDENTITY_UTILS', 'advertiser-portal-pwa.error.getUserInfo', traceId], {
      level: SystemEventLevel.CRITICAL,
      error: serializeError(error),
    });
    throw error;
  }
};

export const getToken = async (serviceName: string, request: IdentityUtilsRequest): Promise<string | undefined> => {
  const traceId = safeGetTraceId(request);
  try {
    const config = getConfig();
    const defaultApiClientCredential = config.catalyst.register['hapi-eg-authorization'].options.clientCredential;
    const defaultApiClientSecret = config.catalyst.register['hapi-eg-authorization'].options.clientSecret;
    const token = await getApiClientAccessToken(request as any, defaultApiClientCredential, defaultApiClientSecret);

    return token['access_token'];
  } catch (error) {
    request?.log?.([ERROR, serviceName, 'advertiser-portal-pwa.error.getToken', traceId], {
      message: 'Failed to get access token',
      error: serializeError(error),
    });
  }
};

export const getUserContext = async (
  serviceName: string,
  request: IdentityUtilsRequest,
): Promise<{ userId: string; partnerAccountId: string } | undefined> => {
  const traceId = safeGetTraceId(request);
  try {
    const requestingUserPrincipalToken = await getPrincipalToken(request as any);
    const requestingUserPrincipalJwt = getJwtFromToken(requestingUserPrincipalToken.encodedJwt);
    const userId = requestingUserPrincipalJwt.sub;
    const partnerAccountId = requestingUserPrincipalJwt.partner_account_id;

    return { userId, partnerAccountId };
  } catch (error) {
    request?.log?.([ERROR, serviceName, 'advertiser-portal-pwa.error.getUserContext', traceId], {
      message: 'Failed to get user context',
      error: serializeError(error),
    });
  }
};
